import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchSubscriptionPlans,
  fetchPromotions,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
  calculateFinalPrice,
} from "@/api/subscription-plans";
import { fetchPaymentMethods, fetchCurrencies } from "@/api/payments";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Tag } from "lucide-react";
import { PlanCard } from "@/components/subscription_plans/PlanCard";
import { SubscriptionPlan, PromoDiscount } from "@/types/subscription";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SubscriptionPlansList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedPromo, setSelectedPromo] = useState<PromoDiscount | null>(
    null
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<SubscriptionPlan | null>(
    null
  );

  // Fetch subscription plans
  const { data: plans = [], isLoading: isLoadingPlans } = useQuery({
    queryKey: ["subscriptionPlans"],
    queryFn: fetchSubscriptionPlans,
  });

  // Fetch promotions
  const { data: promotions = [], isLoading: isLoadingPromos } = useQuery({
    queryKey: ["promotions"],
    queryFn: fetchPromotions,
  });

  // Helper function to get the lowest price for a plan
  const getLowestPriceForPlan = (plan: SubscriptionPlan): number => {
    if (!plan.priceConfigurations || plan.priceConfigurations.length === 0) {
      return 0;
    }

    // Filter active price configurations
    const activeConfigs = plan.priceConfigurations.filter((pc) =>
      "isActive" in pc ? pc.isActive : true
    );
    if (activeConfigs.length === 0) return 0;

    // Find the configuration with the lowest price after applying plan discount
    const lowestPrice = Math.min(
      ...activeConfigs.map((config) => {
        const basePrice = config.price;
        // Apply plan discount
        const discountPercentage = plan.planDiscountPercentage || 0;
        if (discountPercentage > 0) {
          return basePrice * (1 - discountPercentage / 100);
        }
        return basePrice;
      })
    );

    return lowestPrice;
  };

  // Sort plans by price from lowest to highest
  const sortedPlans = useMemo(() => {
    // Create a copy to avoid mutating the original array
    const plansToSort = [...plans];

    return plansToSort.sort((planA, planB) => {
      // Get the lowest price configuration for each plan
      const priceA = getLowestPriceForPlan(planA);
      const priceB = getLowestPriceForPlan(planB);

      // Sort by price
      return priceA - priceB;
    });
  }, [plans]);

  // Update plan mutation
  const updatePlanMutation = useMutation({
    mutationFn: (params: { id: number; data: Partial<SubscriptionPlan> }) =>
      updateSubscriptionPlan(params.id, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionPlans"] });
      toast({
        title: "Success",
        description: "Plan updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete plan mutation
  const deletePlanMutation = useMutation({
    mutationFn: deleteSubscriptionPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionPlans"] });
      toast({
        title: "Success",
        description: "Plan deleted successfully",
      });
      setIsDeleteDialogOpen(false);
      setPlanToDelete(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleToggleActive = async (plan: SubscriptionPlan) => {
    try {
      await updatePlanMutation.mutateAsync({
        id: plan.id,
        data: { isActive: !plan.isActive },
      });
    } catch (error) {
      console.error("Error toggling plan status:", error);
    }
  };

  const handleDelete = async (plan: SubscriptionPlan) => {
    setPlanToDelete(plan);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (planToDelete) {
      deletePlanMutation.mutate(planToDelete.id);
    }
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    navigate(`/admin/subscription-plans/${plan.id}`);
  };

  // Filter to only show truly active promotions
  const currentActivePromos = promotions.filter(
    (promo) =>
      promo.isActive &&
      (!promo.endDate || new Date(promo.endDate) > new Date()) &&
      promo.currentUses < (promo.maxUses || Infinity)
  );

  // Check if a plan is applicable for the selected promotion
  const isPlanApplicable = (planId: number) => {
    if (!selectedPromo) return false;
    return selectedPromo.applicablePlans.includes(planId);
  };

  if (isLoadingPlans || isLoadingPromos) {
    return (
      <AdminLayout title="Subscription Plans">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </AdminLayout>
    );
  }

  const activePlans = sortedPlans.filter((plan) => plan.isActive);
  const inactivePlans = sortedPlans.filter((plan) => !plan.isActive);

  return (
    <AdminLayout title="Subscription Plans">
      <div className="mb-4">
        <p className="text-muted-foreground text-sm">
          Manage subscription plans that users can purchase for accessing the
          platform
        </p>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="space-y-1">
            <DialogTitle>Delete Plan</DialogTitle>
            <DialogDescription className="text-xs">
              Are you sure you want to delete the plan "{planToDelete?.name}"?
              This action cannot be undone and may affect users with active
              subscriptions.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-1 sm:gap-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={confirmDelete}
              disabled={deletePlanMutation.isPending}
            >
              {deletePlanMutation.isPending ? (
                <>
                  <span className="mr-2">Deleting...</span>
                  <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promotion selector */}
      {currentActivePromos.length > 0 && (
        <div className="bg-muted/40 p-4 rounded-lg mb-4">
          <h2 className="text-sm font-medium mb-2 flex items-center gap-2">
            <Tag className="h-4 w-4" />
            Apply Promotion
          </h2>
          <div className="flex flex-wrap gap-2">
            {currentActivePromos.map((promo) => (
              <Button
                key={promo.id}
                variant={selectedPromo?.id === promo.id ? "default" : "outline"}
                size="sm"
                onClick={() =>
                  setSelectedPromo(
                    selectedPromo?.id === promo.id ? null : promo
                  )
                }
              >
                {promo.name}
                {promo.discountType === "percentage" ? (
                  <Badge className="ml-2 bg-orange-100 text-orange-700 border-none">
                    {promo.discountValue}% off
                  </Badge>
                ) : (
                  <Badge className="ml-2 bg-orange-100 text-orange-700 border-none">
                    ₹{promo.discountValue.toFixed(2)} off
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Plans grid - using tabs to separate active and inactive plans */}
      <Tabs defaultValue="active" className="w-full">
        <TabsList className="w-full grid grid-cols-2 h-10">
          <TabsTrigger value="active" className="text-sm">
            Active Plans
            <Badge className="ml-2 bg-green-100 text-green-700 border-none">
              {activePlans.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="inactive" className="text-sm">
            Inactive Plans
            <Badge className="ml-2 bg-gray-100 text-gray-700 border-none">
              {inactivePlans.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          {activePlans.length === 0 ? (
            <p className="text-muted-foreground text-center py-6 text-sm">
              No active plans found.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activePlans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleActive={handleToggleActive}
                  activePromotions={currentActivePromos}
                  selectedPromotion={selectedPromo}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inactive" className="mt-4">
          {inactivePlans.length === 0 ? (
            <p className="text-muted-foreground text-center py-6 text-sm">
              No inactive plans found.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {inactivePlans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleActive={handleToggleActive}
                  activePromotions={currentActivePromos}
                  selectedPromotion={selectedPromo}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default SubscriptionPlansList;
